import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {assert} from 'Asserts';
import {BenchlingState} from 'redux-stores/reduceApp';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import * as StageRunActions from 'workflows/StageRunActions';
import {getOtherParentOutputSamples} from 'workflows/util/StageRunUtil';
import {
  STAGE_RUN_FULL_SHAPE,
  STAGE_RUN_SAMPLE_FULL_SHAPE,
  StageRun,
  StageRunSample,
} from 'workflows/WorkflowConstants';

import ExternalInputPicker, {
  ExternalInputSpecifierType,
  getInputSamplesFromSpecifier,
  InputType,
} from './ExternalInputPicker';

interface ConnectedProps {
  parentIdsByStageRunId: {[id: string]: Array<string>} | null;
  stageRunsById: {[id: string]: StageRun} | null;
}

interface AddInputSampleModalProps {
  stageRuns: Array<StageRun>;
  stageRunSampleById: {[id: string]: StageRunSample};
  onRequestClose: () => void;
  initialExternalState?: {
    stageRunId: string;
    inputType: InputType | null;
  };
}

interface State {
  stageRun: StageRun | null;
  parentStageRun: StageRun | null;
  isExternalSelected: boolean;
  externalInputSpecifier: ExternalInputSpecifierType | null;
  sample: StageRunSample | null;
}

function mapStateToProps(state: BenchlingState, ownProps: AddInputSampleModalProps): ConnectedProps {
  const {stageRuns} = ownProps;
  const workflowSelector = new WorkflowSelector(state.models.workflows);
  const parentIdsByStageRunId = workflowSelector.getParentIdsByStageRunId(
    stageRuns.map((stageRun) => stageRun.id)
  );
  if (!parentIdsByStageRunId) {
    return {
      parentIdsByStageRunId: null,
      stageRunsById: null,
    };
  }
  const stageRunIds = [
    ...Object.keys(parentIdsByStageRunId),
    // eslint-disable-next-line no-restricted-syntax
    ..._.flatten(Object.values(parentIdsByStageRunId)),
  ];
  return {
    parentIdsByStageRunId,
    stageRunsById: workflowSelector.getStageRunsById(stageRunIds),
  };
}

/**
 * Allow adding input samples for initial or non-initial stage runs.
 * The input selector switches between a batch search and a dropdown of the parent's output samples,
 * depending on whether the stage run is initial or non-initial.
 */
class AddInputSampleModal extends React.Component<AddInputSampleModalProps & ConnectedProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    // Must be non-empty.
    stageRuns: PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE.isRequired).isRequired,
    stageRunSampleById: PropTypes.objectOf(STAGE_RUN_SAMPLE_FULL_SHAPE).isRequired,
    onRequestClose: PropTypes.func.isRequired,

    // Optionally initialize the modal with "external" selected and a particular external input type.
    initialExternalState: PropTypes.shape({
      stageRunId: PropTypes.string.isRequired,
      inputType: PropTypes.oneOf([InputType.BATCH, InputType.FILE, InputType.CONTAINER]),
    }),

    // Props provided by connect
    stageRunsById: PropTypes.objectOf(STAGE_RUN_FULL_SHAPE.isRequired),
    parentIdsByStageRunId: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string.isRequired).isRequired),
  };

  constructor(props: AddInputSampleModalProps & ConnectedProps) {
    super(props);
    assert(this.props.stageRuns.length > 0, 'The stageRuns prop must be non-empty.');
    let initialState: State;
    if (this.props.initialExternalState) {
      initialState = {
        stageRun: _.findWhere(this.props.stageRuns, {id: this.props.initialExternalState.stageRunId}) || null,
        parentStageRun: null,
        isExternalSelected: true,
        externalInputSpecifier: null,
        sample: null,
      };
    } else {
      initialState = {
        stageRun: null,
        parentStageRun: null,
        isExternalSelected: false,
        externalInputSpecifier: null,
        sample: null,
      };
    }
    this.state = this._resolveDefaults(initialState);
  }

  _resolveDefaults(state: State): State {
    // Fill in the stage run (should always be possible).
    if (!state.stageRun) {
      state = {...state, stageRun: this.props.stageRuns[0]};
    }

    // Fill in the the input source if it's not there (should always be possible).
    if (!state.parentStageRun && !state.isExternalSelected) {
      const parentStageRuns = this._getParentStageRuns(state.stageRun!);
      if (parentStageRuns.length > 0) {
        state = {...state, parentStageRun: parentStageRuns[0]};
      } else {
        state = {...state, isExternalSelected: true};
      }
    }

    // Fill in the sample if we can.
    if (state.parentStageRun && !state.sample) {
      const otherParentOutputSamples = getOtherParentOutputSamples(state.stageRun!, {
        parentStageRun: state.parentStageRun,
        stageRunSampleById: this.props.stageRunSampleById,
      });
      if (otherParentOutputSamples.length > 0) {
        state = {...state, sample: otherParentOutputSamples[0]};
      }
    }

    return state;
  }

  _getParentStageRuns(stageRun: StageRun): Array<StageRun> {
    if (!this.props.parentIdsByStageRunId || !this.props.stageRunsById) {
      return [];
    }
    const parentStageRunIds = this.props.parentIdsByStageRunId[stageRun.id];
    return parentStageRunIds.map((parentId: string) => this.props.stageRunsById![parentId]);
  }

  _isInputValid(): boolean {
    if (this.state.sample) {
      return true;
    } else if (this.state.externalInputSpecifier) {
      return getInputSamplesFromSpecifier(this.state.externalInputSpecifier).length > 0;
    } else {
      return false;
    }
  }

  _getButtons(): Array<ModalButton> {
    return [
      {
        text: 'Add',
        submit: true,
        style: ButtonStyle.PRIMARY,
        disabled: !this._isInputValid(),
        key: 'add',
      },
    ];
  }

  _handleSubmit = async (): Promise<void> => {
    if (!this._isInputValid() || !this.state.stageRun) {
      return;
    }
    if (this.state.sample) {
      StageRunActions.replaceInputSample(this.state.stageRun.id, {
        sampleIdToAdd: this.state.sample.id,
      });
    } else if (this.state.externalInputSpecifier) {
      const samplesToAdd = getInputSamplesFromSpecifier(this.state.externalInputSpecifier);

      await withLoadingScreen(StageRunActions.bulkAddInputSamples(this.state.stageRun.id, {samplesToAdd}));
    } else {
      assert(false, 'Expected either sample or externalInputSpecifier to be set when submitting.');
    }
    this.props.onRequestClose();
  };

  _handleSelectStageRun = (stageRunId: string): void => {
    const stageRun = this.props.stageRuns.find((run) => run.id === stageRunId) || null;
    this.setState(
      this._resolveDefaults({
        ...this.state,
        stageRun,
        parentStageRun: null,
        isExternalSelected: false,
        externalInputSpecifier: null,
        sample: null,
      })
    );
  };

  _handleSelectParentStageRun(parentStageRun: StageRun): void {
    this.setState(
      this._resolveDefaults({
        ...this.state,
        parentStageRun,
        isExternalSelected: false,
        externalInputSpecifier: null,
        sample: null,
      })
    );
  }

  _handleSetExternalSelected = (): void => {
    this.setState(
      this._resolveDefaults({
        ...this.state,
        parentStageRun: null,
        isExternalSelected: true,
        externalInputSpecifier: null,
        sample: null,
      })
    );
  };

  _renderStageRunSelector(): JSX.Element {
    return (
      <SelectDropdown
        displayName={this.state.stageRun ? this.state.stageRun.name : 'Select stage run'}
        menu={
          <SearchableMenu
            isMulti={false}
            onSelect={(selectedValue) => {
              this._handleSelectStageRun(selectedValue);
            }}
            optionDatas={this.props.stageRuns.map((stageRun) => ({
              name: stageRun.name,
              value: stageRun.id,
            }))}
            selectedValue={this.state.stageRun ? this.state.stageRun.id : null}
            width={MenuWidth.FULL}
          />
        }
      />
    );
  }

  _renderInputSourceSelector(): JSX.Element | null {
    const {stageRun, parentStageRun} = this.state;
    const parentStageRuns = stageRun ? this._getParentStageRuns(stageRun) : [];
    return (
      <SelectDropdown
        displayName={parentStageRun ? parentStageRun.name : 'External'}
        menu={
          <Menu width={MenuWidth.MEDIUM}>
            {parentStageRuns.map((parent) => (
              <Option
                key={parent.id}
                onSelect={() => {
                  this._handleSelectParentStageRun(parent);
                }}
              >
                {parent.name}
              </Option>
            ))}
            <Option
              onSelect={() => {
                this._handleSetExternalSelected();
              }}
            >
              External
            </Option>
          </Menu>
        }
      />
    );
  }

  _renderInputSelector(): JSX.Element {
    if (this.state.parentStageRun && this.state.stageRun) {
      assert(
        !this.state.isExternalSelected,
        'Exactly one of parentStageRun or isExternalSelected should be specified.'
      );

      const samples = getOtherParentOutputSamples(this.state.stageRun, {
        parentStageRun: this.state.parentStageRun,
        stageRunSampleById: this.props.stageRunSampleById,
      });
      if (samples.length === 0) {
        return <SelectDropdown displayName="No available samples" disabled={true} menu={<></>} />;
      } else {
        assert(Boolean(this.state.sample), 'Sample should be autofilled from _resolveDefaults.');
        return (
          <SelectDropdown
            displayName={this.state.sample ? this.state.sample.name : ''}
            menu={
              <Menu width={MenuWidth.MEDIUM}>
                {samples.map((sample) => (
                  <Option
                    key={sample.id}
                    onSelect={() => {
                      this.setState({sample});
                    }}
                  >
                    {sample.name}
                  </Option>
                ))}
              </Menu>
            }
          />
        );
      }
    } else {
      assert(
        this.state.isExternalSelected,
        'Exactly one of parentStageRun or isExternalSelected should be specified.'
      );
      return (
        <ExternalInputPicker
          externalInputSpecifier={this.state.externalInputSpecifier}
          onSetExternalInputSpecifier={(externalInputSpecifier: ExternalInputSpecifierType) => {
            this.setState({externalInputSpecifier});
          }}
          allowContainers={true}
          initialInputType={
            this.props.initialExternalState == null ? null : this.props.initialExternalState.inputType
          }
        />
      );
    }
  }

  render(): JSX.Element {
    return (
      <Modal
        title="Add input sample"
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this._handleSubmit}
        buttons={this._getButtons()}
      >
        <div className="u-spacing-bottom">
          <FieldLabel label="Stage run" />
          {this._renderStageRunSelector()}
        </div>
        <div className="u-spacing-bottom">
          <FieldLabel label="Source" />
          {this._renderInputSourceSelector()}
        </div>
        <div>
          <FieldLabel label="Input" />
          {this._renderInputSelector()}
        </div>
      </Modal>
    );
  }
}

export default connect(mapStateToProps)(AddInputSampleModal);
